import * as actionTypes from './actionTypes';
import checkHttpError from './checkHttpError';
import responseToJson from './responseToJson';
import buildPrefillFieldValues from '../lib/formstack/buildPrefillFieldValues';
import withTimeout from '../lib/withTimeout';
import Config from '../Config';

const config = new Config();

// Bounds the wait on the one call that gates the embedded form. Sized above the FR-015 3s p95
// interactive budget so ordinary variance does not hard-fail prefill, and below the hand-off's
// 15s because this call sits on the applicant's path to an interactive screen rather than
// after a deliberate submit. Revisit once the staging latency FR-015 calls out is resolved.
export const FORMSTACK_PREFILL_TIMEOUT_MS = 10000;

// `checkHttpError` attaches the whole `Response` to the error it throws. On this request the
// body is the PII prefill payload and the headers carry the Ellis auth token, so that object
// must not reach Redux, Datadog, or a console serialization. Keep the message and the status —
// enough for the FR-008 error state to be specific — and drop everything else.
function toSafeError(error) {
  const status = error && error.response ? error.response.status : undefined;
  const safeError = new Error((error && error.message) || 'The Formstack prefill request failed.');
  if (status !== undefined) safeError.status = status;
  // Carried across deliberately: a timeout has no HTTP status, so without the tag it is
  // indistinguishable downstream from a genuine network failure and both collapse into the
  // same RUM bucket. It is a boolean, not vendor data, so it is safe to keep.
  if (error && error.isTimeout) safeError.isTimeout = true;
  return safeError;
}

export function createFormstackPrefillRequest() {
  return {
    type: actionTypes.CREATE_FORMSTACK_PREFILL_REQUEST,
  };
}

export function createFormstackPrefillSuccess(result) {
  const { id, url } = result || {};
  return {
    type: actionTypes.CREATE_FORMSTACK_PREFILL_SUCCESS,
    id: id || null,
    url: url || null,
  };
}

export function createFormstackPrefillError(error) {
  return {
    type: actionTypes.CREATE_FORMSTACK_PREFILL_ERROR,
    error,
  };
}

function buildRequestOptions(state) {
  return {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: state.authToken,
    },
    body: JSON.stringify({ fields: buildPrefillFieldValues(state) }),
    mode: 'cors',
  };
}

// Creates a Formstack prefill session via the `ellis` proxy. The authorised field set is built
// from the store by buildPrefillFieldValues and sent in the POST body — never in the URL
// (FR-011) — and the auth token travels as a header (FR-014).
export function createFormstackPrefill(timeoutMs = FORMSTACK_PREFILL_TIMEOUT_MS) {
  return (dispatch, getState) => {
    dispatch(createFormstackPrefillRequest());

    const state = getState();
    const endpoint = `${config.apiUrl}/applications/${state.applicationId}/formstack-prefill`;

    return withTimeout(
      // Deferred so that a synchronous throw — building the body, or fetch itself — still
      // becomes a rejection. Otherwise it would escape `dispatch` uncaught, leaving no error
      // action dispatched and nothing for the FR-008 error state to react to.
      Promise.resolve()
        .then(() => fetch(endpoint, buildRequestOptions(state)))
        .then(checkHttpError)
        .then(responseToJson),
      timeoutMs,
    )
      .then((json) => {
        dispatch(createFormstackPrefillSuccess(json));
        return json;
      })
      .catch((error) => {
        const safeError = toSafeError(error);
        dispatch(createFormstackPrefillError(safeError));
        // Rejecting is required by the repo's action contract: resolving here would let the
        // container navigate into the embedded form as though prefill had succeeded.
        return Promise.reject(safeError);
      });
  };
}
